import { createContext } from "preact";
import type { ComponentChildren } from "preact";
import { useContext, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { ToastViewport, type ToastEntry } from "../components/ui/toast";
import type { AlertTone } from "../components/ui/alert";

type ToastOptions = { duration?: number };

export type ToastApi = {
  show: (tone: AlertTone, message: string, opts?: ToastOptions) => void;
  success: (message: string, opts?: ToastOptions) => void;
  info: (message: string, opts?: ToastOptions) => void;
  warning: (message: string, opts?: ToastOptions) => void;
  error: (message: string, opts?: ToastOptions) => void;
  dismiss: (id: string) => void;
};

// Errors get the longest window since they carry recovery instructions; every
// toast also has a close button, so long stays are skippable.
const DEFAULT_DURATION: Record<AlertTone, number> = {
  success: 2500,
  info: 4000,
  warning: 6000,
  danger: 8000,
};
const MAX_TOASTS = 4;
// Must match the exit `duration-200` in ToastViewport.
const EXIT_MS = 200;

const ToastContext = createContext<ToastApi | null>(null);

// Provider for transient action feedback ("保存しました", deploy results, …).
// Mounted once in app.tsx; rendering happens in a fixed overlay so firing a
// toast never shifts document layout, unlike the inline Alerts it replaces.
// SSR-safe: timers only start inside show(), which never runs during the
// preact-iso prerender (no event handlers fire there).
export function ToastProvider({ children }: { children: ComponentChildren }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef(new Map<string, number>());
  const idRef = useRef(0);

  useEffect(() => {
    return () => {
      for (const t of timersRef.current.values()) clearTimeout(t);
    };
  }, []);

  const api = useMemo<ToastApi>(() => {
    function clearTimer(id: string) {
      const t = timersRef.current.get(id);
      if (t !== undefined) {
        clearTimeout(t);
        timersRef.current.delete(id);
      }
    }

    function remove(id: string) {
      clearTimer(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }

    // Two-phase removal: flag `leaving` so the CSS exit transition plays,
    // then drop the entry once it has finished.
    function startLeave(id: string) {
      clearTimer(id);
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)),
      );
      timersRef.current.set(
        id,
        window.setTimeout(() => remove(id), EXIT_MS),
      );
    }

    function show(tone: AlertTone, message: string, opts?: ToastOptions) {
      const id = String(++idRef.current);
      setToasts((prev) => {
        // Replace an identical toast (and restart its clock via the new entry)
        // so rapid repeats — e.g. double-save — don't stack duplicates.
        const next = prev.filter(
          (t) => !(t.tone === tone && t.message === message),
        );
        // Cap the stack, dropping the oldest outright.
        while (next.length >= MAX_TOASTS) next.shift();
        // Timers of dropped entries fire into a list that no longer contains
        // their id — harmless no-ops, cleaned up on dismiss/unmount anyway.
        return [...next, { id, tone, message, leaving: false }];
      });
      timersRef.current.set(
        id,
        window.setTimeout(
          () => startLeave(id),
          opts?.duration ?? DEFAULT_DURATION[tone],
        ),
      );
    }

    return {
      show,
      success: (m, o) => show("success", m, o),
      info: (m, o) => show("info", m, o),
      warning: (m, o) => show("warning", m, o),
      error: (m, o) => show("danger", m, o),
      dismiss: startLeave,
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={api.dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
